/**
 * The connector picker, and the SIWE prompt behind it.
 *
 * Two modals, consecutive, deliberately not merged — docs/04 §3.1:
 *
 * > Steps 2 and 3 are presented as two consecutive modals, not merged. Wallets
 * > render EIP-4361 messages beautifully and that materially reduces user fear;
 * > a fused custom struct would lose it.
 *
 * So this component gets the user to a connected wallet and then hands off. The
 * SIWE message itself is rendered *by the wallet*, not by us, which is the point
 * — the user reads it in the one piece of UI on screen that we do not control.
 */

import { Button, Modal, Spinner } from '@/components/primitives'

export interface ConnectorOption {
  id: string
  name: string
  /** Data-URI icon supplied by EIP-6963. Never a remote URL — the CSP has no
   *  `img-src` entry for one, and an icon that 404s looks like a broken wallet. */
  icon?: string | undefined
}

export interface ConnectModalProps {
  open: boolean
  onClose: () => void
  connectors: readonly ConnectorOption[]
  onSelect: (id: string) => void
  busy?: boolean
  error?: string | null
}

export function ConnectModal({
  open,
  onClose,
  connectors,
  onSelect,
  busy,
  error,
}: ConnectModalProps) {
  return (
    <Modal open={open} onClose={onClose} title="Connect a wallet">
      {/* docs/07 §6's SIWE framing, shown *before* the wallet opens rather than
          after — "this costs no gas" answers the question at the moment the user
          is deciding whether to click, not once they are staring at a signature
          request. */}
      <p className="text-caption text-text-secondary">
        Connecting doesn&apos;t cost gas and isn&apos;t a transaction.
      </p>

      <div className="mt-3 space-y-1">
        {connectors.length === 0 ? (
          <p className="rounded-sm bg-bg-raised px-2 py-3 text-center text-caption text-text-secondary">
            No wallet detected. Install a browser wallet, or use Coinbase Wallet.
          </p>
        ) : (
          connectors.map((connector) => (
            <button
              key={connector.id}
              type="button"
              disabled={busy}
              onClick={() => onSelect(connector.id)}
              className="flex w-full items-center gap-2 rounded-sm border border-border-default bg-bg-raised px-2 py-2 text-body text-text-primary transition-colors duration-[var(--ud-duration-instant)] hover:bg-bg-hover hover:border-border-strong focus-visible:shadow-focus focus-visible:outline-none disabled:opacity-55"
            >
              {connector.icon ? (
                <img src={connector.icon} alt="" aria-hidden="true" className="size-4 rounded-xs" />
              ) : (
                <span aria-hidden="true" className="size-4 rounded-xs bg-bg-active" />
              )}
              <span className="flex-1 text-left">{connector.name}</span>
              {busy ? <Spinner /> : null}
            </button>
          ))
        )}
      </div>

      {error ? (
        <p className="mt-3 rounded-sm bg-danger-bg px-2 py-1_5 text-caption text-danger-text">
          {error}
        </p>
      ) : null}
    </Modal>
  )
}

export interface SignInModalProps {
  open: boolean
  onClose: () => void
  onConfirm: () => void
  busy?: boolean
  error?: string | null
}

/** docs/07 §6: `Sign in to UpTik` / `This doesn't cost gas and isn't a transaction.` */
export function SignInModal({ open, onClose, onConfirm, busy, error }: SignInModalProps) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Sign in to UpTik"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" loading={busy ?? false} onClick={onConfirm}>
            Sign in
          </Button>
        </div>
      }
    >
      <p className="text-body text-text-primary">
        This doesn&apos;t cost gas and isn&apos;t a transaction.
      </p>
      <p className="mt-2 text-caption text-text-secondary">
        Your wallet will show you the exact message you&apos;re signing. It names
        this site, expires in five minutes, and can only be used once.
      </p>
      {error ? (
        <p className="mt-3 rounded-sm bg-danger-bg px-2 py-1_5 text-caption text-danger-text">
          {error}
        </p>
      ) : null}
    </Modal>
  )
}
